import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Badge, Spinner, Modal, Form, OverlayTrigger, Tooltip, Nav } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { DUYET_NOILAYSOLIEU_API, BaseGatewayImage } from '@app/utils/apiUrl';
import { trangThaiLaySoLieuValue, objectTrangThaiLaySoLieu } from '@app/utils/define';
import { Get, Put } from '@app/utils/httpProvider';
import moment from 'moment';
import { toast } from 'react-toastify';
import NoiLaySoLieuEdit from './NoiLaySoLieuEdit';

const DuyetNoiLaySoLieu = ({ hoSoId, activeTab, setActiveTab }) => {
    const [showModal, setShowModal] = useState(false);
    const [viewMode, setViewMode] = useState('list');
    const [dataEdit, setDataEdit] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [trangThai, setTrangThai] = useState('');
    const [ghiChu, setGhiChu] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const { isLoading, data: queryData, refetch } = useQuery(
        ['duyetSoLieuNghienCuuData', hoSoId],
        () => Get(DUYET_NOILAYSOLIEU_API.GET_PAGING, null, { HoSoNghienCuuSinhId: hoSoId, PageIndex: 1, PageSize: 100 }),
        { 
            enabled: !!hoSoId,
            keepPreviousData: true
        }
    );

    let dataList = [];
    if (queryData) {
        if (Array.isArray(queryData)) {
            dataList = queryData;
        } else if (queryData.data && Array.isArray(queryData.data)) {
            dataList = queryData.data;
        } else if (queryData.data?.$values) {
            dataList = queryData.data.$values;
        } else if (queryData.$values) {
            dataList = queryData.$values;
        }
    }

    useEffect(() => {
        if (dataList && dataList.length > 0) {
            setSelectedRow(prev => {
                if (prev) {
                    const fresh = dataList.find(item => item.id === prev.id);
                    return fresh || dataList[0];
                }
                return dataList[0];
            });
        } else {
            setSelectedRow(null);
        }
    }, [dataList]);

    const handleAction = (item) => {
        setDataEdit(item);
        setTrangThai(item.trangThai || '');
        setGhiChu(item.ghiChuChinhSua || '');
        setShowModal(true);
    };

    const renderTooltip = (props, text) => (
        <Tooltip id="button-tooltip" {...props}>
            {text}
        </Tooltip>
    );

    const submitDuyet = async () => {
        if (!trangThai) {
            toast.warning('Vui lòng chọn trạng thái!');
            return;
        }

        const payload = {
            HoSoNghienCuuSinhId: hoSoId,
            NoiLay: dataEdit.noiLay || '',
            TuNgay: dataEdit.tuNgay || null,
            DenNgay: dataEdit.denNgay || null,
            FileCuDiLay: dataEdit.fileCuDiLay || '',
            TrangThai: trangThai,
            GhiChuChinhSua: ghiChu
        };

        setIsSaving(true);
        try {
            const res = await Put(DUYET_NOILAYSOLIEU_API.DUYET_HO_SO, dataEdit.id, payload);
            if (res && (res.status === 200 || res.status === 201 || res.status === 204 || res.id || res.success)) {
                toast.success('Lưu thành công!');
                refetch();
                setShowModal(false);
            } else {
                toast.error(res?.message || 'Có lỗi xảy ra');
            }
        } catch (error) {
            toast.error(error?.message || 'Có lỗi xảy ra');
        } finally {
            setIsSaving(false);
        }
    };

    const renderTrangThai = (val) => {
        switch (val) {
            case trangThaiLaySoLieuValue.choTiepNhan: return <Badge variant="warning">Chờ tiếp nhận</Badge>;
            case trangThaiLaySoLieuValue.tiepNhanHoSo: return <Badge variant="info">Tiếp nhận hồ sơ</Badge>;
            case trangThaiLaySoLieuValue.chinhSua: return <Badge variant="secondary">Yêu cầu chỉnh sửa</Badge>;
            case trangThaiLaySoLieuValue.dangLaySoLieu: return <Badge variant="primary">Đang lấy số liệu</Badge>;
            case trangThaiLaySoLieuValue.hoanThanh: return <Badge variant="success">Hoàn thành</Badge>;
            case trangThaiLaySoLieuValue.tuChoi: return <Badge variant="danger">Từ chối</Badge>;
            default: return <Badge variant="secondary">Chưa xác định</Badge>;
        }
    };

    return (
        <>
        <Card className="h-100 card-sticky-layout">
            <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                    <Nav variant="pills" activeKey={activeTab} onSelect={(k) => setActiveTab && setActiveTab(k)} className="flex-row">
                        <Nav.Item>
                            <Nav.Link eventKey="noiLaySoLieu" className="font-weight-bold">Nơi lấy số liệu</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="quaTrinhLaySoLieu" className="font-weight-bold">Quá trình lấy số liệu</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="kiemTraSoLieu" className="font-weight-bold">Kiểm tra số liệu</Nav.Link>
                        </Nav.Item>
                    </Nav>
                </div>
            </Card.Header>
            <Card.Body>
                {isLoading ? (
                    <div className="text-center p-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <div className="table-responsive flex-grow-1">
                        <Table striped bordered hover>
                            <thead>
                                <tr>
                                    <th className="text-center" width="50">STT</th>
                                    <th>Nơi lấy số liệu</th>
                                    <th className="text-center">Từ ngày</th>
                                    <th className="text-center">Đến ngày</th>
                                    <th className="text-center">File đính kèm</th>
                                    <th className="text-center">Trạng thái</th>
                                    <th>Ghi chú</th>
                                </tr>
                            </thead>
                        <tbody>
                            {dataList && dataList.length > 0 ? (
                                dataList.map((item, index) => (
                                    <tr 
                                        key={item.id}
                                        onClick={() => setSelectedRow(item)}
                                        style={{ cursor: 'pointer' }}
                                        className={selectedRow?.id === item.id ? 'table-primary' : ''}
                                    >
                                        <td className="text-center">{index + 1}</td>
                                        <td>{item.noiLay}</td>
                                        <td className="text-center">
                                            {item.tuNgay ? moment(item.tuNgay).format('DD/MM/YYYY') : ''}
                                        </td>
                                        <td className="text-center">
                                            {item.denNgay ? moment(item.denNgay).format('DD/MM/YYYY') : ''}
                                        </td>
                                        <td className="text-center">
                                            {item.fileCuDiLay ? (
                                                (() => {
                                                    const path = item.fileCuDiLay;
                                                    const baseUrl = BaseGatewayImage.endsWith('/') ? BaseGatewayImage.slice(0, -1) : BaseGatewayImage;
                                                    const safePath = path.startsWith('/') ? path : `/${path}`;
                                                    const fileUrl = path.startsWith('http') ? path : `${baseUrl}${safePath}`;
                                                    let name = path.split('/').pop() || 'File';
                                                    name = name.replace(/_\d{14}(?=\.\w+$|$)/, '');
                                                    return (
                                                        <a href={fileUrl} target="_blank" rel="noreferrer" className="btn btn-outline-primary btn-sm text-left mb-1 mr-1" style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }} title={name}>
                                                            <i className="fas fa-paperclip mr-1"></i> {name}
                                                        </a>
                                                    );
                                                })()
                                            ) : (
                                                <span className="text-muted">Không có</span>
                                            )}
                                        </td>
                                        <td className="text-center">{renderTrangThai(item.trangThai)}</td>
                                        <td>{item.ghiChuChinhSua}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="text-center text-muted">
                                        Chưa có dữ liệu Nơi lấy số liệu
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                    </div>
                )}
            </Card.Body>
            {!isLoading && (
                <Card.Footer className="bg-white card-footer-sticky">
                    <div className="d-flex justify-content-end pr-4">
                        <Button 
                            variant="warning" 
                            size="sm" 
                            onClick={() => handleAction(selectedRow)} 
                            className="mr-2 text-white"
                            disabled={!selectedRow}
                        >
                            <i className="fas fa-tasks mr-1"></i> Cập nhật trạng thái
                        </Button>
                        <Button 
                            variant="info" 
                            size="sm" 
                            onClick={() => { setDataEdit(selectedRow); setViewMode('edit'); }}
                            disabled={!selectedRow}
                        >
                            <i className="fas fa-edit mr-1"></i> Sửa
                        </Button>
                    </div>
                </Card.Footer>
            )}
        </Card>

        {showModal && (
            <Modal show={showModal} onHide={() => setShowModal(false)} backdrop="static" keyboard={false} size="md">
                <Modal.Header closeButton className="bg-primary text-white">
                    <Modal.Title>
                        Cập nhật trạng thái
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group>
                            <Form.Label>Trạng thái <span className="text-danger">*</span></Form.Label>
                            <Form.Control as="select" value={trangThai} onChange={(e) => setTrangThai(e.target.value)}>
                                {objectTrangThaiLaySoLieu.map((st) => (
                                    <option key={st.value} value={st.value}>{st.label}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Ghi chú {trangThai === trangThaiLaySoLieuValue.tuChoi || trangThai === trangThaiLaySoLieuValue.chinhSua ? <span className="text-danger">*</span> : ''}</Form.Label>
                            <Form.Control 
                                as="textarea" 
                                rows={3} 
                                placeholder="Nhập ghi chú (nếu có)..." 
                                value={ghiChu}
                                onChange={(e) => setGhiChu(e.target.value)}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)} disabled={isSaving}>
                        Hủy
                    </Button>
                    <Button variant="primary" onClick={submitDuyet} disabled={isSaving || ((trangThai === trangThaiLaySoLieuValue.tuChoi || trangThai === trangThaiLaySoLieuValue.chinhSua) && !ghiChu.trim())}>
                        {isSaving ? 'Đang lưu...' : 'Lưu lại'}
                    </Button>
                </Modal.Footer>
            </Modal>
        )}
        
        {viewMode === 'edit' && dataEdit && (
            <NoiLaySoLieuEdit hoSoId={hoSoId} dataEdit={dataEdit} setViewMode={setViewMode} refreshList={refetch} />
        )}
        </>
    );
};

export default DuyetNoiLaySoLieu;
